import { useEffect } from 'react';
import { RouteComponentProps, Link, useNavigate } from '@reach/router';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import { PolicyStreamPageContainer } from './components/PageContainer';
import { Form, FormState } from 'informed';
import { usePolicyStreamGoogleAnalytics } from '../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../hooks/usePolicyStreamGoogleAnalytics/types';
import { withFormStyles, WithFormStyles } from '../../util/withFormStyles';
import {
  NextButton,
  BackButton,
  PolicyStreamButtonsContainer,
} from './components';
import { policyStreamPaths } from './policyStreamConstants';
import { PolicyStreamLayout } from './components';
import { useSubmitPolicyStreamQuote } from '../../hooks';
import { useEnsureFormPresent } from '../../hooks/useEnsureFormPresent';
import { POLICYSTREAM_FORMS } from './constants';
import RenderedQuestionInstance from '../common/QuoteComponents/RenderedQuestionInstance';
import { useDispatch, useSelector } from 'react-redux';
import {
  answerInstancesToFormState,
  formStateToAnswerInstances,
} from '../QuoteWizard/QuoteForms/utility';
import {
  generateSubmitPolicyStreamFormAnswersThunk,
  generateGetAnsweredForm,
  getAnsweredForms,
} from '../../store/policystreamStore';
import { useScrollToTopOnInitialMount } from '@calefy-inc/hooks';
import { PolicyStreamFormCard } from './components/PolicyStreamFormCard';
import {
  MissingInformation,
  useMissingPlanSelectionGuard,
} from './components/MissingInformation';
import { AgreeToTosComponent } from './components/AgreeToTosComponent';

interface PolicyStreamPostPlanSelectionInformationPageProps
  extends RouteComponentProps,
    WithFormStyles {}
export const PolicyStreamPostPlanSelectionInformationPage = withFormStyles()(
  ({ classes }: PolicyStreamPostPlanSelectionInformationPageProps) => {
    const myAnalytics = usePolicyStreamGoogleAnalytics();

    useScrollToTopOnInitialMount();
    const dispatch = useDispatch();
    const submitQuote = useSubmitPolicyStreamQuote({ status: 'IN_PROGRESS' });

    // Send Google Analytics event on component load
    useEffect(() => {
      myAnalytics?.triggerEvent(
        GAEventNames.PostPlanSelectionInformation,
        EventActions.PageLoad,
        'PolicyStream Post Plan Selection Information Page',
      );
    }, [myAnalytics]);
    const basicClientInfoForm = useEnsureFormPresent({
      internalName: POLICYSTREAM_FORMS.basicClientInfo,
    });
    const basicClientInfoAnswers = useSelector(
      generateGetAnsweredForm({
        internalName: POLICYSTREAM_FORMS.basicClientInfo,
      }),
    );
    const answeredForms = useSelector(getAnsweredForms);
    const navigate = useNavigate();
    const initialValues = basicClientInfoAnswers
      ? answerInstancesToFormState(basicClientInfoAnswers.answers)
      : undefined;

    const missingPlanSelection = useMissingPlanSelectionGuard();

    return (
      <PolicyStreamLayout>
        {missingPlanSelection ? (
          <MissingInformation
            missing={missingPlanSelection.missing}
            redirectURL={policyStreamPaths.planSelection.url}
            stepName='Plan Selection'
          />
        ) : (
          <PolicyStreamPageContainer>
            <Typography>
              Great! We just need a bit more information from you to continue
              the process.
            </Typography>
            <PolicyStreamFormCard>
              <Form
                className={classes.form}
                initialValues={initialValues}
                onSubmit={(formState) => {
                  const { values } = formState;
                  if (!basicClientInfoForm) {
                    return;
                  }
                  const answers = formStateToAnswerInstances(
                    values,
                    basicClientInfoForm?.questionInstances,
                  );
                  dispatch(
                    generateSubmitPolicyStreamFormAnswersThunk({
                      form: basicClientInfoForm,
                      answers,
                    }),
                  );
                  const answeredBasicClientInfoForm =
                    basicClientInfoForm.toDefaultUnifiedCompletedForm();
                  answeredBasicClientInfoForm.answers = answers.map((answer) =>
                    answer.toUnifiedAnswerInstance(),
                  );
                  submitQuote({
                    formsWithAnswers: [
                      ...answeredForms
                        .filter(
                          (form) =>
                            form.form.internalName !==
                            POLICYSTREAM_FORMS.basicClientInfo,
                        )
                        .map((form) => form.toUnifiedCompletedForm()),
                      answeredBasicClientInfoForm,
                    ],
                  });
                  navigate(policyStreamPaths.planSpecificQuestions.url);
                }}
              >
                {({ formState }: { formState: FormState }) => {
                  return (
                    <>
                      {basicClientInfoForm
                        ? basicClientInfoForm.questionInstances.map((q) => (
                            <RenderedQuestionInstance
                              key={q.id}
                              questionInstance={q}
                              classes={classes}
                            />
                          ))
                        : null}
                      <Collapse in={Object.values(formState.values).length > 0}>
                        <AgreeToTosComponent />
                      </Collapse>
                      <PolicyStreamButtonsContainer
                        sx={{
                          mt: 2,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <BackButton
                          // @ts-expect-error
                          component={Link}
                          to={policyStreamPaths.planSelection.url}
                        />
                        <NextButton type='submit' />
                      </PolicyStreamButtonsContainer>
                    </>
                  );
                }}
              </Form>
            </PolicyStreamFormCard>
          </PolicyStreamPageContainer>
        )}
      </PolicyStreamLayout>
    );
  },
);
